import { AccountClient, AccountEnumClient } from '@frontend/account/api';
import { InitializeOptions } from '@frontend/account/types';
import { ApiError } from '@frontend/api-utils';
import { ErrorHandler } from '@frontend/error-handler';
import { ToastUtil } from '@frontend/toast-utils';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

interface ViewProps {
    initializeOptions: InitializeOptions;
    setInitializeOptions: React.Dispatch<React.SetStateAction<InitializeOptions>>;
    onSubmit: () => Promise<void>;
    onCancel: () => void;
    applications: string[];
    customers: string[];
}

const useAccountInitialize = (): ViewProps => {
    const { accountId } = useParams();
    const [applications, setApplications] = useState<string[]>([]);
    const [customers, setCustomers] = useState<string[]>([]);
    const [initializeOptions, setInitializeOptions] = useState<InitializeOptions>({
        applications: [],
        customers: []
    });
    const navigate = useNavigate();

    useEffect(() => {
        AccountEnumClient.fetchInitializeApplications().then(setApplications);
        AccountEnumClient.fetchInitializeCustomers().then(setCustomers);
    }, []);

    const onSubmit = async () => {
        if (!accountId) {
            ToastUtil.error(
                <FormattedMessage
                    id='account-initialize.error.missing-account-id'
                    defaultMessage='Missing account ID'
                />
            );
            return;
        }

        try {
            await AccountClient.initializeAccount(accountId, initializeOptions);
            ToastUtil.success(
                <FormattedMessage
                    id='account-initialize.success'
                    defaultMessage='Account initialized successfully'
                />
            );
            navigate(-1);
        } catch (err) {
            ErrorHandler.handleError({ error: err as Error | ApiError });
        }
    };

    const onCancel = () => {
        navigate(-1);
    };

    return {
        initializeOptions,
        setInitializeOptions,
        onCancel,
        onSubmit,
        applications,
        customers
    };
};

export default useAccountInitialize;
